import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { useDeviceDetection } from '../utils/deviceDetection';
import { useUserStore } from '../stores/userStore';
import Sidebar from '../components/dashboard/Sidebar';
import MobileDashboard from '../components/mobile/MobileDashboard';
import DashboardHome from '../components/dashboard/DashboardHome';
import ContentIdeas from '../components/dashboard/ContentIdeas';
import Analytics from '../components/dashboard/Analytics';
import Settings from '../components/dashboard/Settings';
import Help from '../components/dashboard/Help';
import DashboardPricing from '../components/dashboard/DashboardPricing';
import SavedPlans from '../components/dashboard/SavedPlans';
import Calendar from '../components/dashboard/calendar/Calendar';
import LiveChatSupport from '../components/dashboard/support/LiveChatSupport';
import OnboardingModal from '../components/onboarding/OnboardingModal';
import LoadingScreen from '../components/LoadingScreen';

interface User {
  name: string;
  isNewUser: boolean;
}

export default function DashboardPage() {
  const isMobile = useDeviceDetection();
  const isPro = useUserStore(state => state.isPro);
  const isAdmin = useUserStore(state => state.isAdmin);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const isOnboardingCompleted = useUserStore(state => state.isOnboardingCompleted);
  const setOnboardingComplete = useUserStore(state => state.setOnboardingComplete);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const user: User = {
    name: 'João Silva',
    isNewUser: true
  };

  // Check if user has access to Pro support (either Pro user or Admin)
  const hasProSupport = isPro() || isAdmin();

  // Show onboarding modal for new users
  useEffect(() => {
    if (!isOnboardingCompleted) {
      setShowOnboarding(true);
    }
  }, [isOnboardingCompleted]);

  // Auto-show live chat when dashboard loads for Pro users and Admins
  useEffect(() => {
    if (hasProSupport) {
      // Add a small delay to ensure the dashboard is fully loaded
      const timer = setTimeout(() => {
        setShowLiveChat(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [hasProSupport]);

  // Add a loading state to ensure components are properly mounted
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500); // Increased timeout for better loading experience
    return () => clearTimeout(timer);
  }, []);

  const handleOnboardingComplete = () => {
    setOnboardingComplete(true);
    setShowOnboarding(false);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isMobile) {
    return <MobileDashboard />;
  }

  return (
    <div className="flex h-screen bg-primary-50">
      {/* Sidebar */}
      <div className="w-64 bg-primary-50">
        <Sidebar />
      </div>
      
      {/* Main content */}
      <main className="flex-1 overflow-y-auto bg-primary-50 p-8">
        <div className="bg-white rounded-2xl shadow-sm">
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/" element={<DashboardHome user={user} />} />
              <Route path="/ideias" element={<ContentIdeas user={user} />} />
              <Route path="/calendario" element={<Calendar />} />
              <Route path="/analises" element={<Analytics />} />
              <Route path="/precos" element={<DashboardPricing />} />
              <Route path="/planos" element={<SavedPlans />} />
              <Route path="/configuracoes" element={<Settings user={user} />} />
              <Route path="/ajuda" element={<Help />} />
            </Routes>
          </Suspense>
        </div>
      </main>
      
      {/* Live Chat Support Button (Pro users and Admins) */}
      {hasProSupport && !showLiveChat && (
        <button
          onClick={() => setShowLiveChat(true)}
          className="fixed bottom-6 right-6 bg-indigo-600 text-white rounded-full p-4 shadow-lg hover:bg-indigo-700 transition-colors z-50 animate-pulse"
          title="Suporte ao vivo"
        >
          <MessageSquare className="h-6 w-6" />
        </button>
      )}
      
      {/* Live Chat Support Modal */}
      {showLiveChat && <LiveChatSupport onClose={() => setShowLiveChat(false)} />}

      {/* Onboarding Modal for new users */}
      {showOnboarding && <OnboardingModal onComplete={handleOnboardingComplete} />}
    </div>
  );
}